import { normalizeProfileSystemId } from './vmkProductSpec';
import { BTW_MULTIPLIER_INCL } from './pdfPricing';

const round2 = (value) => Math.round(Number(value) * 100) / 100;

/** Automatic discount when VEKA (synego) profile is selected. */
export const VEKA_PROFILE_DISCOUNT_PERCENT = 4;

export const isVekaProfileSystem = (profileSystemId) =>
  normalizeProfileSystemId(profileSystemId) === 'synego';

export const calcVekaProfileDiscountAmount = (price, profileSystemId) => {
  if (!isVekaProfileSystem(profileSystemId) || !(Number(price) > 0)) return 0;
  return Math.round(Number(price) * (VEKA_PROFILE_DISCOUNT_PERCENT / 100) * 100) / 100;
};

export const applyVekaProfileDiscount = (price, profileSystemId) => {
  const discount = calcVekaProfileDiscountAmount(price, profileSystemId);
  return Number(price) - discount;
};

/** Apply admin pricing: markup margin, per-window costs, surcharge, then silent deduction. */
export function applyUnitPriceAdjustments(baseUnitPrice, pricingSettings, { isAuthenticated = false, parts = 1 } = {}) {
  if (!isAuthenticated || !pricingSettings) return baseUnitPrice;

  let price = baseUnitPrice;

  if (pricingSettings.margin_enabled && pricingSettings.margin_percentage > 0) {
    price *= 1 + pricingSettings.margin_percentage / 100;
  }

  if (pricingSettings.workers_fixing_enabled && (pricingSettings.workers_fixing_amount ?? 0) > 0) {
    price += Number(pricingSettings.workers_fixing_amount) || 0;
  }

  if (pricingSettings.material_cost_enabled && (pricingSettings.material_cost_amount ?? 0) > 0) {
    price += Number(pricingSettings.material_cost_amount) || 0;
  }

  if (pricingSettings.surcharge_enabled && pricingSettings.surcharge_amount > 0) {
    price += pricingSettings.surcharge_amount * (parts || 1);
  }

  if (pricingSettings.deduction_margin_enabled && pricingSettings.deduction_margin_percentage > 0) {
    const pct = Math.min(100, Math.max(0, Number(pricingSettings.deduction_margin_percentage) || 0));
    price *= 1 - pct / 100;
  }

  return price;
}

/** Silent percentage reduction applied after markup and per-window add-ons, before visible quote discount. */
export function applyDeductionMargin(price, pricingSettings, { isAuthenticated = false } = {}) {
  if (!isAuthenticated || !pricingSettings?.deduction_margin_enabled || !(pricingSettings.deduction_margin_percentage > 0)) {
    return price;
  }
  const pct = Math.min(100, Math.max(0, Number(pricingSettings.deduction_margin_percentage) || 0));
  return price * (1 - pct / 100);
}

/** Max fixed EUR deduction an admin can apply on the export-quote screen (per quote). */
export const MAX_EXPORT_DEDUCTION_EUR = 500;

export function clampExportDeductionEur(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.min(MAX_EXPORT_DEDUCTION_EUR, Math.round(n * 100) / 100);
}

/** Subtract a fixed export deduction (ex VAT) from a quote grand total (ex VAT). */
export function applyExportDeductionEur(totalExVat, enabled, amountExVat) {
  if (!enabled) return { total: totalExVat, deduction: 0 };
  const deduction = clampExportDeductionEur(amountExVat);
  return { total: Math.max(0, Number(totalExVat) - deduction), deduction };
}

/**
 * Per-quote export deduction as entered by admin — incl. BTW (matches visible quote totals).
 * Stored on clientDetails.exportDeductionAmount.
 */
export function getQuoteExportDeductionInclVat(clientDetails) {
  if (!clientDetails?.exportDeductionEnabled) return 0;
  return clampExportDeductionEur(clientDetails.exportDeductionAmount);
}

/** Ex-VAT portion of the export deduction (for ex-VAT subtotal math). */
export function getQuoteExportDeductionExVat(clientDetails) {
  const incl = getQuoteExportDeductionInclVat(clientDetails);
  if (incl <= 0) return 0;
  return round2(incl / BTW_MULTIPLIER_INCL);
}
